import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import {
  registerServiceWorker,
  isStandalone,
  notificationsSupported,
  notificationPermission,
  requestNotificationPermission,
  showLocalNotification,
  onUpdateAvailable,
  applyPwaUpdate,
  repairAppCache,
  hardReload,
  type NotifPermission,
} from '@/lib/pwa';
import { countQueued, syncOfflineQueue } from '@/lib/offlineQueue';
import { ensureWebPushSubscription, registerPeriodicSync } from '@/lib/webPush';
import { recordPwaInstall } from '@/lib/pwaInstalls';
import { appendCurrencyEquivalence, getDisplayCurrency } from '@/lib/currency';
import {
  computeAppBadgeCount,
  fetchLatestNotifications,
  registerPushDevice,
  setAppBadge,
  touchPushDevice,
  notificationTitle,
  notificationTargetUrl,
  type NotifRow,
} from '@/lib/pushDevices';
import { fetchOneSignalConfig, initOneSignal, loginOneSignal, logoutOneSignal, requestOneSignalPermission } from '@/lib/onesignal';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PwaContextValue {
  online: boolean;
  pending: number;
  syncing: boolean;
  canInstall: boolean;
  installed: boolean;
  notifications: NotifPermission | 'unsupported';
  updateReady: boolean;
  badgeCount: number;
  deviceRegistered: boolean;
  install: () => Promise<void>;
  registerDevice: () => Promise<void>;
  enableNotifications: () => Promise<void>;
  testNotification: () => Promise<void>;
  syncNow: () => Promise<void>;
  applyUpdate: () => Promise<void>;
  forceUpdate: () => Promise<void>;
}

const PwaContext = createContext<PwaContextValue | null>(null);

const DISMISS_KEY = 'zifek-pwa-install-dismissed';

export function PwaProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // Sur un site public (boutique d'un marchand), on n'affiche jamais la
  // bannière « Mise à jour disponible » : elle est réservée aux espaces
  // connectés (dashboard marchand / compte client).
  const { isTenant } = useTenant();
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [notifPerm, setNotifPerm] = useState<NotifPermission | 'unsupported'>(() => notificationPermission());
  const [updateReady, setUpdateReady] = useState(false);
  const [badgeCount, setBadgeCount] = useState(0);
  const [deviceRegistered, setDeviceRegistered] = useState(false);
  const [nsEngine, setNsEngine] = useState(false);
  const lastNotifIdRef = useRef<number | null>(null);

  // ── Enregistrement du service worker + détection d'installation ──
  useEffect(() => {
    registerServiceWorker();
    setInstalled(isStandalone());
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === '1');
    } catch { /* stockage indisponible */ }

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // ── Suivi des installations (compteur SuperAdmin) ──
  useEffect(() => {
    const onInstalled = () => { void recordPwaInstall(user?.id ?? null); };
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, [user?.id]);

  // ── Mises à jour de l'application (nouvelles pages / nouvelle version) ──
  // Dès qu'une nouvelle version est prête, on affiche une bannière « Mettre à
  // jour » (au lieu d'écraser la page en cours). On revérifie aussi au retour
  // sur l'onglet et toutes les 30 minutes.
  useEffect(() => {
    const off = onUpdateAvailable(() => setUpdateReady(true));
    const check = async () => {
      if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
      if (document.visibilityState !== 'visible') return;
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        await reg?.update();
      } catch { /* ignore */ }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    const interval = window.setInterval(check, 30 * 60 * 1000);
    return () => {
      off();
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(interval);
    };
  }, []);

  const applyUpdate = useCallback(async () => {
    setUpdateReady(false);
    await applyPwaUpdate();
  }, []);

  // « Forcer la mise à jour » directement depuis la bannière : vide le cache,
  // récupère la dernière version publiée puis recharge l'application.
  const forceUpdate = useCallback(async () => {
    setUpdateReady(false);
    try {
      await repairAppCache();
    } finally {
      hardReload(400);
    }
  }, []);

  const refreshPending = useCallback(() => {
    countQueued().then(setPending).catch(() => {});
  }, []);

  useEffect(() => {
    refreshPending();
    const handler = () => refreshPending();
    window.addEventListener('zifek-queue-changed', handler);
    return () => window.removeEventListener('zifek-queue-changed', handler);
  }, [refreshPending]);

  const syncNow = useCallback(async () => {
    if (!user?.id) return;
    const n = await countQueued();
    if (n === 0) return;
    setSyncing(true);
    try {
      await syncOfflineQueue(user.id);
      refreshPending();
      window.dispatchEvent(new CustomEvent('zifek-synced'));
    } finally {
      setSyncing(false);
    }
  }, [user, refreshPending]);

  // ── Détection réseau ──
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      syncNow();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [syncNow]);

  // ── Système de notifications interne du PWA ──
  // 1) enregistre l'appareil (code unique = compte + empreinte navigateur),
  // 2) entretient le badge du nombre de notifications sur l'icône de l'app,
  // 3) interroge les nouvelles notifications et les affiche en notification
  //    système (façon WhatsApp) quand l'app n'est pas au premier plan.
  useEffect(() => {
    if (!user?.id) return;
    let active = true;

    const refreshBadge = async () => {
      // Badge façon WhatsApp : messages privés + notifications non lues.
      const count = await computeAppBadgeCount(user.id);
      if (!active) return;
      setBadgeCount(count);
      void setAppBadge(count);
      void touchPushDevice(user.id, { badgeCount: count });
    };

    const checkNew = async (showSystem: boolean) => {
      const rows: NotifRow[] = await fetchLatestNotifications(user.id, 5);
      if (!active) return;
      const newestId = rows.length ? Math.max(...rows.map((r) => r.id)) : null;
      if (showSystem && !nsEngine && lastNotifIdRef.current != null && newestId != null) {
        const fresh = rows
          .filter((r) => r.id > (lastNotifIdRef.current as number))
          .sort((a, b) => a.id - b.id);
        for (const n of fresh) {
          const body = appendCurrencyEquivalence(n.message || '', user?.monaie || 'MAD', getDisplayCurrency())
            || 'Vous avez une nouvelle notification.';
          await showLocalNotification(notificationTitle(n.type), body, {
            tag: `zifek-${n.id}`,
            // Ouvre directement la page concernée (commande, message, réservation…).
            url: notificationTargetUrl(n.type),
          });
        }
      }
      if (newestId != null) lastNotifIdRef.current = newestId;
      await refreshBadge();
    };

    // Enregistrement de l'appareil (canal de notification interne).
    void registerPushDevice({
      userId: user.id,
      accountType: user.type,
      notificationsEnabled: notificationPermission() === 'granted',
    }).then((device) => { if (active && device) setDeviceRegistered(true); });

    void checkNew(false);
    const interval = window.setInterval(() => {
      // Notification système uniquement quand l'utilisateur n'est pas dessus.
      void checkNew(document.visibilityState !== 'visible');
    }, 30000);

    const onVisible = () => { void checkNew(false); };
    document.addEventListener('visibilitychange', onVisible);

    // Réception instantanée tant que l'app est ouverte.
    const channel = supabase
      .channel('pwa-notifications')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        () => { void checkNew(document.visibilityState !== 'visible'); },
      )
      .subscribe();

    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      void supabase.removeChannel(channel);
    };
  }, [user, nsEngine]);

  // ── Badge de l'icône « façon WhatsApp » ──
  // Le numéro affiché sur l'icône de l'application (et dans l'espace de
  // notification) doit se mettre à jour aussitôt qu'un nouveau message, une
  // nouvelle commande ou toute autre notification arrive, même sans ouvrir la
  // conversation. On écoute donc en temps réel les messages privés ET les
  // notifications, plus un rafraîchissement au retour au premier plan.
  useEffect(() => {
    if (!user?.id) return;
    let active = true;

    const refresh = async () => {
      const count = await computeAppBadgeCount(user.id);
      if (!active) return;
      setBadgeCount(count);
      void setAppBadge(count);
      void touchPushDevice(user.id, { badgeCount: count });
    };

    const channel = supabase
      .channel('pwa-badge')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'messages', filter: `receiver_id=eq.${user.id}` },
        () => { void refresh(); },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        () => { void refresh(); },
      )
      .subscribe();

    const onChanged = () => { void refresh(); };
    window.addEventListener('notifications-changed', onChanged);
    window.addEventListener('messages-changed', onChanged);
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      active = false;
      void supabase.removeChannel(channel);
      window.removeEventListener('notifications-changed', onChanged);
      window.removeEventListener('messages-changed', onChanged);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);

  // ── Web Push : abonnement réel aux notifications (reçues même app fermée) ──
  // On maintient TOUJOURS l'abonnement Web Push natif, même quand OneSignal est
  // activé : les deux moteurs fonctionnent en parallèle. Ainsi, si OneSignal est
  // mal configuré (clé REST absente, external_id non associé…), les
  // notifications continuent d'arriver par le Web Push natif — plus de
  // dépendance à un seul canal.
  useEffect(() => {
    if (!user?.id) return;
    const sync = () => {
      if (notificationPermission() === 'granted') {
        void ensureWebPushSubscription(user.id);
      }
    };
    sync();
    void registerPeriodicSync();
    const onVisible = () => { if (document.visibilityState === 'visible') sync(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user]);

  // Messages du service worker : ré-abonnement push / vérification périodique.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const type = (event.data as { type?: string } | null)?.type;
      if (type === 'PUSH_RESUBSCRIBE' && user?.id) void ensureWebPushSubscription(user.id);
      if (type === 'PERIODIC_CHECK') window.dispatchEvent(new Event('zifek-periodic-check'));
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [user]);

  // ── OneSignal (si activé côté SuperAdmin) ──
  // Lorsque OneSignal est activé et configuré, TOUT le système de notification
  // bascule dessus (push en arrière-plan + autorisation) ; sinon on utilise le
  // système interne (Web Push VAPID + notifications locales).
  useEffect(() => {
    if (!user?.id) {
      setNsEngine(false);
      void logoutOneSignal();
      return;
    }
    let active = true;
    void (async () => {
      const cfg = await fetchOneSignalConfig();
      const usable = !!cfg && cfg.enabled && !!cfg.appId;
      if (!active) return;
      setNsEngine(usable);
      if (!usable) return;
      const ok = await initOneSignal(cfg!.appId, cfg!.safariWebId);
      if (ok && active) await loginOneSignal(user.id);
    })();
    return () => { active = false; };
  }, [user]);

  // Enregistre l'appareil courant (code unique) — appelé à l'installation,
  // à la procédure iPhone, et à l'activation des notifications.
  const registerDevice = useCallback(async () => {
    if (!user?.id) return;
    const device = await registerPushDevice({
      userId: user.id,
      accountType: user.type,
      notificationsEnabled: notificationPermission() === 'granted',
    });
    if (device) setDeviceRegistered(true);
  }, [user]);

  // ── Déclencheur d'installation depuis n'importe où dans l'app ──
  const install = useCallback(async () => {
    void registerDevice();
    if (!installEvent) return;
    try {
      await installEvent.prompt();
      await installEvent.userChoice;
    } finally {
      setInstallEvent(null);
    }
  }, [installEvent, registerDevice]);

  useEffect(() => {
    const handler = () => { install(); };
    window.addEventListener('zifek-install-prompt', handler);
    return () => window.removeEventListener('zifek-install-prompt', handler);
  }, [install]);

  const enableNotifications = useCallback(async () => {
    // Moteur OneSignal actif : on demande l'autorisation via OneSignal.
    if (nsEngine) {
      await requestOneSignalPermission();
      if (user?.id) {
        await registerPushDevice({ userId: user.id, accountType: user.type, notificationsEnabled: true });
        setDeviceRegistered(true);
      }
      void showLocalNotification('Notifications activées', 'Vous recevrez désormais vos alertes ici, même app fermée.', { tag: 'zifek-welcome' });
      return;
    }
    const result = await requestNotificationPermission();
    setNotifPerm(result);
    if (result === 'granted') {
      if (user?.id) {
        await registerPushDevice({ userId: user.id, accountType: user.type, notificationsEnabled: true });
        await ensureWebPushSubscription(user.id);
        setDeviceRegistered(true);
      }
      void registerPeriodicSync();
      // Test visuel immédiat : confirme à l'utilisateur que tout fonctionne.
      void showLocalNotification('Notifications activées', 'Vous recevrez désormais vos alertes ici, même app fermée.', { tag: 'zifek-welcome' });
    }
  }, [user, nsEngine]);

  // Envoie une notification de test immédiate (vérifie l'affichage système).
  const testNotification = useCallback(async () => {
    let perm = notificationPermission();
    if (perm === 'default') {
      perm = await requestNotificationPermission();
      setNotifPerm(perm);
      if (perm === 'granted' && user?.id) {
        await registerPushDevice({ userId: user.id, accountType: user.type, notificationsEnabled: true });
        setDeviceRegistered(true);
      }
    }
    if (perm !== 'granted') return;
    void setAppBadge(1);
    await showLocalNotification(
      'Test de notification',
      'Parfait ! Vous verrez vos alertes (commandes, messages…) exactement comme ceci.',
      { tag: 'zifek-test' },
    );
  }, [user]);

  const dismissInstall = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* */ }
  };

  const value = useMemo<PwaContextValue>(() => ({
    online,
    pending,
    syncing,
    canInstall: !!installEvent,
    installed,
    notifications: notifPerm,
    updateReady,
    badgeCount,
    deviceRegistered,
    install,
    registerDevice,
    enableNotifications,
    testNotification,
    syncNow,
    applyUpdate,
    forceUpdate,
  }), [online, pending, syncing, installEvent, installed, notifPerm, updateReady, badgeCount, deviceRegistered, install, registerDevice, enableNotifications, testNotification, syncNow, applyUpdate, forceUpdate]);

  // L'invitation à installer l'application est réservée aux utilisateurs
  // CONNECTÉS (compte client / dashboard). Sur les sites publics (accueil,
  // boutique d'un marchand, pages publiques) elle ne doit jamais s'afficher.
  const showInstallBanner = !!user && !installed && !dismissed && (installEvent != null || isIosSafari());

  return (
    <PwaContext.Provider value={value}>
      {children}

      {/* Bannière de mise à jour de l'application (nouvelles pages / version) */}
      {updateReady && !isTenant && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[95] w-[360px] max-w-[calc(100vw-2rem)] bg-background-50 border border-primary-200/70 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-download-cloud-2-line text-lg"></i>
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-bold text-foreground-950">Mise à jour disponible</h4>
              <p className="text-xs text-foreground-500 mt-0.5 leading-snug">
                Une nouvelle version de l&apos;application est prête, avec les dernières pages et améliorations.
                Mettez à jour pour en profiter.
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <button onClick={applyUpdate} className="h-9 px-4 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
                  Mettre à jour
                </button>
                <button onClick={forceUpdate} className="h-9 px-4 rounded-full border border-primary-300 text-primary-700 text-xs font-semibold whitespace-nowrap hover:bg-primary-50 transition-colors cursor-pointer flex items-center gap-1.5">
                  <i className="ri-refresh-line"></i>
                  Forcer la mise à jour
                </button>
                <button onClick={() => setUpdateReady(false)} className="h-9 px-3 rounded-full text-xs font-medium text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bandeau hors-ligne */}
      {!online && !isTenant && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[90] px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-xs font-medium flex items-center gap-2 shadow-lg">
          <i className="ri-wifi-off-line"></i>
          Hors ligne — vos modifications sont enregistrées et seront synchronisées.
        </div>
      )}

      {/* Indicateur de synchronisation */}
      {online && !isTenant && (pending > 0 || syncing) && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[90] px-4 py-2.5 rounded-full bg-accent-500 text-background-50 text-xs font-medium flex items-center gap-2 shadow-lg">
          <i className={syncing ? 'ri-loader-4-line animate-spin' : 'ri-cloud-line'}></i>
          {syncing ? 'Synchronisation en cours…' : `${pending} opération(s) à synchroniser`}
        </div>
      )}

      {/* Bandeau d'installation — uniquement dans un espace connecté, discret, en bas à droite */}
      {showInstallBanner && (
        <div className="fixed bottom-4 right-4 z-[90] w-[260px] max-w-[calc(100vw-2rem)] bg-background-50 border border-background-200/70 rounded-xl p-3.5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-smartphone-line text-lg"></i>
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-bold text-foreground-950">Installer l&apos;application</h4>
              <p className="text-xs text-foreground-500 mt-0.5 leading-snug">
                Utilisez caisse, produits et carnet de crédit même sans internet. Recevez les notifications de commande.
              </p>
              <div className="flex items-center gap-2 mt-3">
                {installEvent ? (
                  <button onClick={install} className="h-9 px-4 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
                    Installer
                  </button>
                ) : (
                  <span className="text-[11px] text-foreground-500">
                    Sur iPhone : <strong>Partager</strong> → <strong>Sur l&apos;écran d&apos;accueil</strong>.
                  </span>
                )}
                <button onClick={dismissInstall} className="h-9 px-3 rounded-full text-xs font-medium text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invitation à activer les notifications */}
      {!!user && installed && notificationsSupported() && notifPerm === 'default' && (
        <div className="fixed bottom-4 left-4 z-[90] w-[280px] max-w-[calc(100vw-2rem)] bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-notification-3-line"></i>
            </div>
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-foreground-950">Activer les notifications</h4>
              <p className="text-xs text-foreground-500 mt-0.5">Soyez alerté à chaque nouvelle commande.</p>
              <button onClick={enableNotifications} className="mt-2.5 h-9 px-4 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer">
                Activer
              </button>
            </div>
          </div>
        </div>
      )}
    </PwaContext.Provider>
  );
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
  return isIos && isSafari;
}

export function usePwa(): PwaContextValue {
  const ctx = useContext(PwaContext);
  if (!ctx) throw new Error('usePwa must be used within a PwaProvider');
  return ctx;
}